import {Component, React, observer, resolveCss} from '$src/handy'
import * as css from './Table.css'
import {ResearchStateTab, IStore} from '$src/bootstrap/Store'
import ReactTable, {Column} from 'react-table'
import {getSnapshot} from 'mobx-state-tree'
import HideColumnIcon from 'react-icons/lib/fa/close'
import {includes} from 'lodash'
import {DateTime} from 'luxon'

interface Props {
  css?: any
  activeTabState: typeof ResearchStateTab.Type
  setHoveredItemIds: (ids: {[id: number]: true}) => void
  hoveredItemIds: {[id: number]: true}
  setActiveItemIds: (ids: {[id: number]: true}) => void
  activeItemIds: {[id: number]: true}
  markerlist: any[]
}

interface State {}

const classes = resolveCss(css, {})

@observer
export default class TableOfComparableSales extends Component<Props, State> {
  tableCells: any[]
  constructor(props: Props, context: any) {
    super(props, context)
    this.state = {}
    this.tableCells = tableCells.map(c => ({...c, store: this.store}))
  }

  getTrProps = (_: unknown, row: $FixMe) => {
    if (!row) return {}
    const {original} = row

    return {
      onClick: () => {
        this.props.setActiveItemIds({[original.id]: true})
      },
      onMouseOver: () => {
        this.props.setHoveredItemIds({[original.id]: true})
      },
      ...classes(
        original.selected === true && 'enabled',
        this.props.activeItemIds[original.id] === true && 'activeRow',
        this.props.hoveredItemIds[original.id] === true && 'hoveredRow',
      ),
    }
  }

  render() {
    const {props} = this
    const foundItems = getSnapshot(props.activeTabState.foundItems)
    const {byId} = foundItems
    const list = foundItems.list.map((id: string) => ({
      ...byId[id],
      selected: foundItems.selectedIdsOldData[id] === true,
    }))
    const hiddenCols = getSnapshot(
      this.store.hiddenColumnsInComparableSalesView.byId,
    )
    const tableCells = this.tableCells.map(c => ({
      ...c,
      show: hiddenCols[c.accessor] !== true,
    }))

    const dataSource = props.markerlist.length > 0 ? props.markerlist : list

    return (
      <ReactTable
        className={css.table}
        data={dataSource}
        defaultFilterMethod={defaultFilterMethod}
        getTrProps={this.getTrProps}
        loading={props.activeTabState.foundItems.queried !== true}
        defaultPageSize={list.length}
        pageSize={props.activeTabState.getSelectedPageSize()}
        pageSizeOptions={[
          10,
          20,
          50,
          ...(list.length > 50 ? [list.length] : []),
        ]}
        onPageSizeChange={pageSize =>
          props.activeTabState.setSelectedPageSize(pageSize)
        }
        multiSort={false}
        columns={[
          {
            Header: '',
            accessor: 'selected',
            sortable: true,
            maxWidth: 40,
            className: css.disabledEnabledCell,
            Cell: ({original}) => {
              return (
                <label
                  className={css.checkBoxLabel}
                  onClick={e => e.stopPropagation()}
                  key={'checkbox-label-' + original.id}
                >
                  <input
                    type="checkbox"
                    checked={original.selected}
                    key={'checkbox-' + original.id}
                    title="Hold ALT/OPTION to select/deselect other sales in the same address"
                    onChange={e => {
                      if (e.nativeEvent.altKey) {
                        // this.multiSelectOrUnselect(original.id, original.selected)
                        props.activeTabState.batchSelectOrUnselectByAddressOldData(
                          original.id,
                          !original.selected,
                        )
                      } else {
                        props.activeTabState.setSelectedForIdOldData(
                          original.id,
                          !original.selected,
                        )
                      }
                    }}
                  />
                </label>
              )
            },
          },
          ...tableCells,
          {
            Header: ColumnHeader('Location'),
            accessor: 'location',
            store: this.store,
            width: 90,
            sortable: true,
            Cell: ({original}) => {
              return (
                <div className={css.location}>
                  <div className={css.pair}>
                    <span>{parseFloat(original.location.lat).toFixed(6)}</span>
                    <span>{parseFloat(original.location.long).toFixed(6)}</span>
                  </div>
                </div>
              )
            },
          },
        ]}
      />
    )
  }
}

const format = (s: unknown, opts: {toFixed?: number}) =>
  typeof s === 'string'
    ? s
    : typeof s === 'number'
    ? typeof opts.toFixed === 'number'
      ? s.toFixed(opts.toFixed)
      : s
    : typeof s === 'boolean'
    ? s
      ? 'Yes'
      : 'No'
    : s === null
    ? 'Unkown'
    : s === undefined
    ? 'Unkown'
    : JSON.stringify(s)

type CustomColumn = Column & {
  headerLabel: string
  isBool?: boolean
  toFixed?: number
  accessor: string
}

const cellDefs: Array<CustomColumn> = [
  {accessor: 'propertyType', headerLabel: 'Property type'},
  {accessor: 'livingArea', headerLabel: 'Living area'},
  {accessor: 'yearOfConstruction', headerLabel: 'Year of construction'},
  {accessor: 'numberOfRooms', headerLabel: '# of rooms'},
  {accessor: 'newlyBuilt', headerLabel: 'Newly built', isBool: true},
  {accessor: 'condition', headerLabel: 'Condition'},
  {accessor: 'plotOwned', headerLabel: 'Plot owned', isBool: true},
  {accessor: 'hasElevator', headerLabel: 'Has elevator', isBool: true},
  {accessor: 'hasSauna', headerLabel: 'Has sauna', isBool: true},
  {accessor: 'apartmentTypeExact', headerLabel: 'Apartment type exact'},
  {accessor: 'roomsDescription', headerLabel: 'Rooms description'},
  {accessor: 'floorNumber', headerLabel: 'Floor number'},
  {accessor: 'priceSqmAdjusted', headerLabel: 'Price sqm adjusted', toFixed: 0},
  {
    accessor: 'priceFreeOfDeptAdjusted',
    headerLabel: 'Price free of dept adjusted',
    toFixed: 0,
  },
  {accessor: 'priceSqm', headerLabel: 'Price sqm', toFixed: 0},
  {accessor: 'priceFreeOfDept', headerLabel: 'Price free of dept', toFixed: 0},
  {accessor: 'municipality', headerLabel: 'Municipality'},
  {accessor: 'postalCode', headerLabel: 'Postal code'},
  {accessor: 'streetAddress', headerLabel: 'Street address'},
  {
    accessor: 'dateOfSale',
    headerLabel: 'Date of sale',
    sortMethod: (a: string, b: string) => {
      const aT = DateTime.fromISO(a)
      const bT = DateTime.fromISO(b)
      return aT > bT ? 1 : aT < bT ? -1 : 0
    },
    Cell: ({original}: {original: $FixMe}) => {
      return DateTime.fromISO(original.dateOfSale).toFormat('dd.LL.yyyy')
    },
  },
]

const ColumnHeader = (headerText: string) => ({
  column,
}: {
  column: {store: IStore; id: string}
}) => {
  const store = column.store

  return (
    <div className={css.columnHeader}>
      {headerText}
      <div
        className={css.columnHeaderCloseButton}
        onClick={() => {
          store.hiddenColumnsInComparableSalesView.add(column.id)
        }}
      >
        <HideColumnIcon />
      </div>
    </div>
  )
}

const tableCells = cellDefs.map(
  (s: CustomColumn): Column => {
    const cell = {
      sortable: true,
      Cell: ({original}: {original: $FixMe}) =>
        format(original[s.accessor as string], s),
      Header: s.Header || ColumnHeader(s.headerLabel),
      filterable: true,
      ...(s.isBool !== true
        ? {}
        : {
            filterMethod: booleanFilterMethod(s.accessor),
          }),
    }

    return {...cell, ...s}
  },
)

export const defaultFilterMethod = (filter: any, row: any, column: Column) => {
  const id = filter.pivotId || filter.id
  return row[id] !== undefined
    ? includes(String(row[id]).toLowerCase(), filter.value.toLowerCase())
    : true
}

export function booleanFilterMethod(accessor: string) {
  return (filter: $FixMe, row: $FixMe) => {
    if (typeof filter.value !== 'string' || filter.value.length === 0)
      return true
    if (filter.value[0].toLowerCase() === 'y') {
      return row[accessor] === true
    }
    if (filter.value[0].toLowerCase() === 'n') {
      return row[accessor] === false
    }
    return true
  }
}



// WEBPACK FOOTER //
// ./src/comparableSales/OldTableOfComparableSales.tsx